import { JWT_TOKEN, LAST_ACTIVITY, PORTFOLIO } from "./constants";

/**
 * The lifecycle of the few localStorage entries that are NOT preferences
 * (SPEC-0061 §4.3): credentials and tenant / environment identity, wiped on
 * logout so a different login or tenant on the same device never inherits them.
 *
 * Every user preference (column layouts, Gantt and view settings, filters, date
 * presets, dashboards, language) lives on the server through the userPreferences
 * client; its pre-release localStorage residue is the one-time import's business
 * (localPreferencesImport) and is never listed here.
 *
 * Not listed either: `username` — kept for user-switch detection, wiped
 * explicitly in SecurityActions; the `offolio_ws_jobs:*` progress buckets —
 * wiped by JobPersistenceService.clearAllPersistedJobs(); device-only state
 * with no identity (pane sizes, debug flags) — never cleared.
 *
 * ⇨ A NEW persisted key is either a preference (declare its family in
 *   userPreferences.registry.js) or session-bound (register it here).
 */
const SESSION_BOUND = {
  keys: [
    JWT_TOKEN,
    LAST_ACTIVITY, // cross-tab idle bookkeeping; must not outlive the session
    PORTFOLIO,
    "token", // SSO bearer written by SsoCallback (the app's canonical key is jwtToken)
    "currentUserWorkInfo", // current-user object incl. collaborator identity
    "term-overrides-cache", // tenant-specific terminology overrides
  ],
  prefixes: [
    "lastTenant:",
    // Generative workspace working state (not preferences): scenario ids are not
    // unique across tenants / environments.
    "gsep-scopes-stale-",
    "gsep-assumptions-locked-",
    "gsep-quantities-snapshot-",
  ],
  suffixes: [],
};

function removeMatching({ keys, prefixes, suffixes }) {
  const exact = new Set(keys);
  try {
    Object.keys(localStorage).forEach((k) => {
      if (
        exact.has(k) ||
        prefixes.some((p) => k.startsWith(p)) ||
        suffixes.some((s) => k.endsWith(s))
      ) {
        localStorage.removeItem(k);
      }
    });
  } catch {
    // localStorage unavailable — nothing to clear
  }
}

/** Wipe credential + tenant/environment-bound entries — call on logout. */
export function clearSessionScopedStorage() {
  removeMatching(SESSION_BOUND);
}
